import React from "react";
import ActionItemsSettingsDangerSection from "./ActionItemsSettingsDangerSection";
import ActionItemsSettingsDraftStyleSection from "./ActionItemsSettingsDraftStyleSection";
import ActionItemsSettingsHubRow from "./ActionItemsSettingsHubRow";
import ActionItemsSettingsMeetingPrefsSection from "./ActionItemsSettingsMeetingPrefsSection";
import ActionItemsSettingsProfileSection from "./ActionItemsSettingsProfileSection";
import DesktopCaptureSettingsSection from "./DesktopCaptureSettingsSection";
import DesktopNotificationSettingsSection from "./DesktopNotificationSettingsSection";
import DesktopUpdateSettingsRow from "./DesktopUpdateSettingsRow";
import PerformerSetupContent from "./PerformerSetupContent";
import SettingsInfoTip from "./shared/SettingsInfoTip";
import SettingsToggleRow from "./shared/SettingsToggleRow";
import { useArchivedTabVisiblePreference } from "./shared/actionItemsArchivedTabPreference";
import {
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_ABOUT_YOU,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_CALENDAR,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_DRAFT_STYLE,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_GMAIL_DRAFTS,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_HUB,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_MEETING_PREFS,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_REFERENCE_DOCS,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_SLACK_SEND,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_TASK_LABELS,
  buildGeneralSettingsHubSummaries,
} from "./settingsGeneralHelpers";
import {
  ACTION_ITEMS_SETTINGS_TAB_GENERAL,
  buildExtensionActionItemsSettingsUrl,
} from "./actionItemHelpers";

/**
 * General settings body — stacked sections on web, hub drill-down in extension.
 *
 * @example
 * <ActionItemsSettingsGeneralPanel
 *   variant="web"
 *   setup={setup}
 *   recipientStyle="concise"
 *   workingHoursStart={9}
 *   workingHoursEnd={12}
 *   onMeetingPreferencesChange={({ workingHoursStart, workingHoursEnd }) => {}}
 * />
 */
const ActionItemsSettingsGeneralPanel = ({
  variant = "web",
  generalScreen = ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_HUB,
  userName = "",
  userEmail = "",
  profileDescription = "",
  profileLoading = false,
  onProfileDescriptionChange,
  onProfileDescriptionBlur,
  setupProfileHighlight = false,
  onSkipProfileSetup,
  setup,
  pendingCount = 0,
  categoryCatalog,
  onCategoryCatalogChange,
  categoryDescriptions,
  onCategoryDescriptionsChange,
  onContextEdited,
  onDriveInputBlur,
  recipientStyle,
  onRecipientStyleChange,
  workingHoursStart = null,
  workingHoursEnd = null,
  onMeetingPreferencesChange,
  onDeleteAccount,
  configLoading = false,
  onHubNavigate,
}) => {
  const isExtension = variant === "extension";
  const [showArchivedTab, setShowArchivedTab] = useArchivedTabVisiblePreference();
  const extensionWebSettingsUrl = buildExtensionActionItemsSettingsUrl({
    tab: ACTION_ITEMS_SETTINGS_TAB_GENERAL,
  });
  const hubSummaries = buildGeneralSettingsHubSummaries({
    profileDescription,
    categoryCatalog,
    recipientStyle,
    workingHoursStart,
    workingHoursEnd,
    gmailReady: setup.gmailReady,
    calendarReady: setup.calendarReady,
    slackReady: setup.slackReady,
    driveInput: setup.driveInput,
    contextFiles: setup.contextFiles,
  });

  const markEdited = () => onContextEdited?.();

  if (isExtension && generalScreen === ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_HUB) {
    return (
      <div className="settings-general-extension-hub">
        <div className="settings-hub-list" role="list" aria-label="General settings">
          <ActionItemsSettingsHubRow
            title="About you"
            summary={hubSummaries.aboutYou}
            onClick={() => onHubNavigate?.(ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_ABOUT_YOU)}
          />
          <ActionItemsSettingsHubRow
            title="Task labels"
            summary={hubSummaries.taskLabels}
            onClick={() => onHubNavigate?.(ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_TASK_LABELS)}
          />
          <ActionItemsSettingsHubRow
            title="Draft style"
            summary={hubSummaries.draftStyle}
            summaryTone="accent"
            onClick={() => onHubNavigate?.(ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_DRAFT_STYLE)}
          />
          <ActionItemsSettingsHubRow
            title="Meeting preferences"
            summary={hubSummaries.meetingPrefs}
            onClick={() => onHubNavigate?.(ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_MEETING_PREFS)}
          />
          <ActionItemsSettingsHubRow
            title="Gmail drafts"
            summary={hubSummaries.gmailDrafts}
            summaryTone={setup.gmailReady ? "" : "warning"}
            onClick={() => onHubNavigate?.(ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_GMAIL_DRAFTS)}
          />
          <ActionItemsSettingsHubRow
            title="Google Calendar"
            summary={hubSummaries.calendar}
            summaryTone={setup.calendarReady ? "" : "warning"}
            onClick={() => onHubNavigate?.(ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_CALENDAR)}
          />
          <ActionItemsSettingsHubRow
            title="Slack"
            summary={hubSummaries.slackSend}
            summaryTone={setup.slackReady ? "" : "warning"}
            onClick={() => onHubNavigate?.(ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_SLACK_SEND)}
          />
          <ActionItemsSettingsHubRow
            title="Reference documents"
            summary={hubSummaries.referenceDocs}
            onClick={() => onHubNavigate?.(ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_REFERENCE_DOCS)}
          />
        </div>
        <a
          className="settings-hub-web-link"
          href={extensionWebSettingsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open full settings in Task Mantis
        </a>
      </div>
    );
  }

  if (isExtension) {
    return (
      <div
        className={
          configLoading ? "business-app-settings-block__content--dimmed" : ""
        }
      >
        {generalScreen === ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_ABOUT_YOU ? (
          <ActionItemsSettingsProfileSection
            variant="extension-piece"
            profileDescription={profileDescription}
            profileLoading={profileLoading}
            onProfileDescriptionChange={onProfileDescriptionChange}
            onProfileDescriptionBlur={onProfileDescriptionBlur}
          />
        ) : null}
        {generalScreen === ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_DRAFT_STYLE ? (
          <ActionItemsSettingsDraftStyleSection
            variant="extension"
            recipientStyle={recipientStyle}
            onStyleChange={onRecipientStyleChange}
          />
        ) : null}
        {generalScreen === ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_MEETING_PREFS ? (
          <ActionItemsSettingsMeetingPrefsSection
            variant="extension"
            workingHoursStart={workingHoursStart}
            workingHoursEnd={workingHoursEnd}
            onChange={onMeetingPreferencesChange}
          />
        ) : null}
        {[
          ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_TASK_LABELS,
          ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_GMAIL_DRAFTS,
          ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_CALENDAR,
          ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_SLACK_SEND,
          ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_REFERENCE_DOCS,
        ].includes(generalScreen) ? (
          <PerformerSetupContent
            setup={setup}
            pendingCount={pendingCount}
            showRunControls={false}
            showCategorySection={generalScreen === ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_TASK_LABELS}
            sectionsLayout="piece"
            activePiece={generalScreen}
            categoryCatalog={categoryCatalog}
            onCategoryCatalogChange={onCategoryCatalogChange}
            categoryDescriptions={categoryDescriptions}
            onCategoryDescriptionsChange={onCategoryDescriptionsChange}
            onContextEdited={markEdited}
            onDriveInputBlur={onDriveInputBlur}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className={configLoading ? "business-app-settings-block__content--dimmed" : ""}>
      <ActionItemsSettingsProfileSection
        variant="web-section"
        userName={userName}
        userEmail={userEmail}
        profileDescription={profileDescription}
        profileLoading={profileLoading}
        onProfileDescriptionChange={onProfileDescriptionChange}
        setupProfileHighlight={setupProfileHighlight}
        onSkipProfileSetup={onSkipProfileSetup}
      />

      <section className="settings-general-section">
        <h3 className="settings-general-section__title">Tasks</h3>
        <div className="settings-general-block settings-general-block--nested">
          <SettingsToggleRow
            label="Show archived"
            description="Include an Archived tab alongside To do and Done on the tasks page."
            pressed={showArchivedTab}
            onChange={setShowArchivedTab}
          />
        </div>
      </section>

      <section className="settings-general-section">
        <h3 className="settings-general-section__title">AI behavior</h3>
        <div className="settings-general-block settings-general-block--nested">
          <div className="settings-general-block__heading-row">
            <h4 className="settings-general-block__subtitle">Task labels</h4>
            <SettingsInfoTip
              label="About task labels"
              text="Labels the AI can assign when categorizing your tasks. Press Enter to add."
            />
          </div>
          <PerformerSetupContent
            setup={setup}
            pendingCount={pendingCount}
            showRunControls={false}
            showCategorySection
            sectionsLayout="piece"
            activePiece={ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_TASK_LABELS}
            categoryCatalog={categoryCatalog}
            onCategoryCatalogChange={onCategoryCatalogChange}
            categoryDescriptions={categoryDescriptions}
            onCategoryDescriptionsChange={onCategoryDescriptionsChange}
            onContextEdited={markEdited}
          />
        </div>
        <ActionItemsSettingsDraftStyleSection
          variant="web"
          recipientStyle={recipientStyle}
          onStyleChange={onRecipientStyleChange}
        />
        <ActionItemsSettingsMeetingPrefsSection
          variant="web"
          workingHoursStart={workingHoursStart}
          workingHoursEnd={workingHoursEnd}
          onChange={onMeetingPreferencesChange}
        />
      </section>

      <section className="settings-general-section">
        <h3 className="settings-general-section__title">AI Actions Assist</h3>
        <p className="settings-general-section__desc">
          Where AI saves drafts, suggests times and sends messages, plus docs it can read.
        </p>
        <PerformerSetupContent
          setup={setup}
          pendingCount={pendingCount}
          showRunControls={false}
          showCategorySection={false}
          sectionsLayout="stacked-sources"
          categoryCatalog={categoryCatalog}
          onCategoryCatalogChange={onCategoryCatalogChange}
          categoryDescriptions={categoryDescriptions}
          onCategoryDescriptionsChange={onCategoryDescriptionsChange}
          onContextEdited={markEdited}
        />
      </section>

      <DesktopCaptureSettingsSection />

      <DesktopNotificationSettingsSection />

      <DesktopUpdateSettingsRow />

      <section className="settings-general-section settings-general-section--account">
        <h3 className="settings-general-section__title settings-general-section__title--danger">
          Account
        </h3>
        <ActionItemsSettingsDangerSection
          userEmail={userEmail}
          onDeleteAccount={onDeleteAccount}
          embedded
        />
      </section>
    </div>
  );
};

export default ActionItemsSettingsGeneralPanel;
